import type { ApiNode } from '@goalbuddy/client';
import { canHaveParent, type NodeType } from '@goalbuddy/core';
import { Inbox, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { areaColor } from '@/lib/area';
import { useStructure } from '@/lib/hooks';
import { ancestorsOf } from '@/lib/tree';
import { cn } from '@/lib/utils';
import { TypeIcon } from './node-meta';

const CONTAINERS = new Set(['area', 'goal', 'result', 'project', 'section', 'list']);

/** Searchable list of every place `node` may be filed under. Calls `onPick(null)` for "no parent". */
export function DestinationPicker({
  node,
  onPick,
  autoFocus = true,
}: {
  node: Pick<ApiNode, 'id' | 'type'>;
  onPick: (parentId: number | null) => void;
  autoFocus?: boolean;
}) {
  const { data: structure } = useStructure();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);

  const options = useMemo(() => {
    const all = structure ?? [];
    const term = q.trim().toLowerCase();
    const items = all
      .filter(
        (p) =>
          p.id !== node.id &&
          CONTAINERS.has(p.type) &&
          canHaveParent(node.type as NodeType, p.type as NodeType),
      )
      .filter((p) => !ancestorsOf(all, p.id).some((a) => a.id === node.id))
      .map((p) => {
        const path = ancestorsOf(all, p.id).map((a) => a.title);
        return { p, path: path.join(' › '), haystack: [...path, p.title].join(' ').toLowerCase() };
      })
      .filter((o) => !term || o.haystack.includes(term))
      .sort((a, b) => (a.path + a.p.title).localeCompare(b.path + b.p.title));
    return items.slice(0, 40);
  }, [structure, node, q]);

  const showInbox =
    canHaveParent(node.type as NodeType, null) &&
    (!q.trim() || 'inbox no parent'.includes(q.trim().toLowerCase()));
  const total = options.length + (showInbox ? 1 : 0);
  const pick = (i: number) => {
    if (showInbox && i === 0) return onPick(null);
    const o = options[i - (showInbox ? 1 : 0)];
    if (o) onPick(o.p.id);
  };

  return (
    <div>
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 focus-within:border-primary">
        <Search size={14} className="text-muted-fg" />
        <input
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, total - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pick(active);
            }
          }}
          placeholder="Search areas, goals, projects, lists…"
          className="h-9 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-fg/70"
        />
      </div>
      <ul className="mt-2 max-h-64 overflow-y-auto" role="listbox">
        {showInbox && (
          <li>
            <button
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm',
                active === 0 && 'bg-accent',
              )}
              onMouseMove={() => setActive(0)}
              onClick={() => pick(0)}
            >
              <Inbox size={15} className="text-muted-fg" /> No parent (inbox)
            </button>
          </li>
        )}
        {options.map((o, idx) => {
          const i = idx + (showInbox ? 1 : 0);
          const color = areaColor(structure, o.p.id);
          return (
            <li key={o.p.id}>
              <button
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm',
                  active === i && 'bg-accent',
                )}
                onMouseMove={() => setActive(i)}
                onClick={() => pick(i)}
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: color ?? 'var(--color-muted-fg)' }}
                />
                <TypeIcon type={o.p.type} size={14} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{o.p.title}</span>
                  {o.path && <span className="block truncate text-xs text-muted-fg">{o.path}</span>}
                </span>
              </button>
            </li>
          );
        })}
        {total === 0 && (
          <li className="px-2.5 py-4 text-center text-sm text-muted-fg">
            No place to file a {node.type} here.
          </li>
        )}
      </ul>
    </div>
  );
}
